/**
 * Plot geometry helpers for xlabs Locate hybrid.
 * See docs/XLABS_LOCATE.md
 */

export function uid(prefix = 'v') {
  return `${prefix}_${Math.random().toString(36).slice(2, 8)}`;
}

function signedArea(pts) {
  let a = 0;
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i];
    const q = pts[(i + 1) % pts.length];
    a += p.x * q.y - q.x * p.y;
  }
  return a / 2;
}

/**
 * Construx [[x,z]] → plan { id, x, y, lotIndex }.
 * Preserves lot index order (required for list ↔ 3D handle sync).
 */
export function toPlanVertices(lotVertices = []) {
  return lotVertices.map(([x, z], i) => ({
    id: `v${i + 1}`,
    x,
    y: -z,
    lotIndex: i,
  }));
}

/** Plan { x, y }[] → Construx [[x,z]] with z = −y. */
export function toLotVertices(planVerts = []) {
  return planVerts.map((v) => [v.x, -v.y]);
}

/** Copy of plan verts forced CCW for inset math. Does not mutate input. */
export function ensureCcw(pts = []) {
  const copy = pts.map((p) => ({ ...p }));
  if (copy.length >= 3 && signedArea(copy) < 0) copy.reverse();
  return copy;
}

/** Build-envelope plan polygon (inset by setback meters). */
export function insetPlan(planVerts, setbackMeters) {
  if (!(setbackMeters > 0) || planVerts.length < 3) {
    return planVerts.map((p) => ({ x: p.x, y: p.y }));
  }
  return offsetPolygon(ensureCcw(planVerts), setbackMeters);
}

export function polygonArea(pts) {
  let a = 0;
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i];
    const q = pts[(i + 1) % pts.length];
    a += p.x * q.y - q.x * p.y;
  }
  return Math.abs(a) / 2;
}

export function polygonPerimeter(pts) {
  let p = 0;
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i];
    const b = pts[(i + 1) % pts.length];
    p += Math.hypot(b.x - a.x, b.y - a.y);
  }
  return p;
}

/** Uniform 2D lot scale about centroid — XZ only (docs/ADU_SITE_PLANNER.md). */
export const LOT_SCALE_MIN = 0.15;
export const LOT_SCALE_MAX = 8;

/**
 * @param {Array<[number, number]>} vertices Construx [[x,z], …]
 * @param {number} factor Multiplier about centroid (1 = unchanged)
 * @returns {Array<[number, number]>}
 */
export function scaleLotVertices2d(vertices = [], factor = 1) {
  if (!vertices.length || !Number.isFinite(factor) || factor === 1) {
    return vertices.map((v) => [...v]);
  }
  const s = Math.min(LOT_SCALE_MAX, Math.max(LOT_SCALE_MIN, factor));
  const n = vertices.length;
  let cx = 0;
  let cz = 0;
  for (let i = 0; i < n; i++) {
    cx += vertices[i][0];
    cz += vertices[i][1];
  }
  cx /= n;
  cz /= n;
  return vertices.map(([x, z]) => [cx + (x - cx) * s, cz + (z - cz) * s]);
}

/**
 * Per-vertex bisector inset. dist > 0 = inset. Assumes CCW winding.
 * @param {{ x: number, y: number }[]} verts
 * @param {number} dist
 */
export function offsetPolygon(verts, dist) {
  const n = verts.length;
  if (n < 3 || !Number.isFinite(dist) || dist === 0) {
    return verts.map((v) => ({ x: v.x, y: v.y }));
  }
  const out = [];
  for (let i = 0; i < n; i++) {
    const prev = verts[(i - 1 + n) % n];
    const cur = verts[i];
    const next = verts[(i + 1) % n];
    const e1 = { x: cur.x - prev.x, y: cur.y - prev.y };
    const e2 = { x: next.x - cur.x, y: next.y - cur.y };
    const l1 = Math.hypot(e1.x, e1.y) || 1;
    const l2 = Math.hypot(e2.x, e2.y) || 1;
    const n1 = { x: -e1.y / l1, y: e1.x / l1 };
    const n2 = { x: -e2.y / l2, y: e2.x / l2 };
    let bx = n1.x + n2.x;
    let by = n1.y + n2.y;
    const bl = Math.hypot(bx, by) || 1;
    bx /= bl;
    by /= bl;
    const cosA = bx * n1.x + by * n1.y || 1;
    const k = dist / Math.max(0.4, cosA);
    out.push({ x: cur.x + bx * k, y: cur.y + by * k });
  }
  return out;
}

/** xlabs Locate presets as plan verts → Construx lot. */
export function presetLot(name) {
  const presets = {
    rectangle: [
      { x: -12, y: -8 },
      { x: 12, y: -8 },
      { x: 12, y: 8 },
      { x: -12, y: 8 },
    ],
    lshape: [
      { x: -14, y: -8 },
      { x: 8, y: -8 },
      { x: 8, y: 0 },
      { x: 14, y: 0 },
      { x: 14, y: 10 },
      { x: -14, y: 10 },
    ],
    trapezoid: [
      { x: -10, y: -9 },
      { x: 14, y: -9 },
      { x: 10, y: 9 },
      { x: -14, y: 9 },
    ],
  };
  const plan = presets[name];
  if (!plan) return null;
  return { vertices: toLotVertices(plan), y: 0 };
}
